import React from 'react';
import { Link } from 'react-router-dom';
import { Search, Leaf, Camera, MapPin, Calculator, Shield } from 'lucide-react';

const HomePage = () => {
  const features = [
    {
      icon: Search,
      title: 'Food Search',
      description: 'Search for foods by name, barcode, brand, or description to find nutrition information.',
      href: '/search',
    },
    {
      icon: Leaf,
      title: 'Free Foods',
      description: 'Discover foods with less than 0.5g protein that are safe for PKU diets.',
      href: '/free-foods',
    },
    {
      icon: Calculator,
      title: 'Exchange Calculator',
      description: 'Calculate protein exchanges and track your daily protein intake.',
      href: '/search',
    },
    {
      icon: Shield,
      title: 'Aspartame Detection',
      description: 'Identify foods containing aspartame to avoid this harmful sweetener.',
      href: '/search',
    },
    {
      icon: Camera,
      title: 'Photo Storage',
      description: 'Store photos of food items for easy identification and reference.',
      href: '/add-food',
    },
    {
      icon: MapPin,
      title: 'Location Tracking',
      description: 'Find where foods are available and track prices across different stores.',
      href: '/search',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-blue-50 py-20">
        <div className="container-responsive text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Track Your PKU Diet with
            <span className="text-primary-600"> Confidence</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            A comprehensive food tracking application designed specifically for PKU patients. 
            Search foods, calculate exchanges, detect aspartame, and manage your nutrition with ease.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/search"
              className="btn-primary btn-lg inline-flex items-center space-x-2"
            >
              <Search className="w-5 h-5" />
              <span>Start Searching</span>
            </Link>
            <Link
              to="/free-foods"
              className="btn-outline btn-lg inline-flex items-center space-x-2"
            >
              <Leaf className="w-5 h-5" />
              <span>Browse Free Foods</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container-responsive">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need for PKU Diet Management
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our comprehensive tools help you make informed decisions about your nutrition 
              and maintain a healthy PKU diet.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <Link
                  key={feature.title}
                  to={feature.href}
                  className="group block"
                >
                  <div className="card p-6 h-full transition-all duration-300 group-hover:shadow-medium group-hover:-translate-y-1">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors">
                      <Icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-responsive">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-primary-600 mb-2">1000+</div>
              <div className="text-gray-600">Food Items</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-success-600 mb-2">500+</div>
              <div className="text-gray-600">Free Foods</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-warning-600 mb-2">24/7</div>
              <div className="text-gray-600">Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="container-responsive text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Take Control of Your PKU Diet?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Join thousands of PKU patients who are already using our app to manage their nutrition 
            and live healthier lives.
          </p>
          <Link
            to="/add-food"
            className="btn btn-lg bg-white text-primary-600 hover:bg-gray-50 inline-flex items-center space-x-2"
          >
            <Camera className="w-5 h-5" />
            <span>Add Your First Food</span>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
